import { z } from 'zod';
import { defaultCountries, parseCountry } from 'react-international-phone';

export const E164_PHONE_REGEX = /^\+[1-9]\d{6,14}$/;

const COUNTRY_DIAL_CODES = new Set(
  defaultCountries.map((country) => `+${parseCountry(country).dialCode}`),
);

/** Normalize display-formatted international phone strings to E.164 when possible. */
export function normalizePhoneToE164(phone: string | null | undefined): string {
  if (!phone) return '';

  const trimmed = phone.trim();
  if (!trimmed || E164_PHONE_REGEX.test(trimmed)) return trimmed;

  if (trimmed.startsWith('+')) {
    const normalized = `+${trimmed.slice(1).replace(/\D/g, '')}`;
    return COUNTRY_DIAL_CODES.has(normalized) ? '' : normalized;
  }

  return trimmed;
}

/** Zod schema for optional E.164 phone fields */
export const phoneSchema = z
  .string()
  .transform((value) => normalizePhoneToE164(value))
  .pipe(z.string().regex(E164_PHONE_REGEX, 'Invalid phone number').or(z.literal('')))
  .optional();

/** Format E.164 phone for display. Falls back to raw value for legacy data. */
export function formatPhoneForDisplay(phone: string | null | undefined): string {
  if (!phone) return '-';
  const normalized = normalizePhoneToE164(phone);
  // E.164 with Indian number: +91XXXXXXXXXX → +91 XXXXX XXXXX
  if (normalized.startsWith('+91') && normalized.length === 13) {
    return `+91 ${normalized.slice(3, 8)} ${normalized.slice(8)}`;
  }
  return phone;
}
